import {useEffect,useMemo,useState} from 'react';

type Location={code:string;name:string;timezone:string};
type Row={
  appointmentId:string;customerId:string;roomId:string;serviceId:string;
  bookedByActorId:string|null;firstVisit:boolean;verifiedMember:boolean|null;
  id:string;State:string;Begins:string;Service:string;Room:string;
  'Customer First Name':string;'Customer Last Name':string;
  'Booked At':string;'Booked By':string;'Cancelled At':string;
  'Paid':string;'Tax':string;'Total Paid':string;
};
type SelectItem={id:string;name:string};
type Data={rows:Row[];rooms:SelectItem[];services:SelectItem[];
  bookers:SelectItem[];summary:{count:number;uniqueCustomers:number;
    totalPaidCents:number|null};incompleteFinancialData:boolean};
type Filters={from:string;to:string;dateField:'beginning'|'booked';
  status:'active'|'cancelled'|'all'|'checked-out'|'checked-in'|'no-show';
  room:string;service:string;first:'any'|'first'|'returning';
  member:'any'|'member'|'non-member';bookedBy:string};
const stateOptions=[
  {value:'active',label:'Active'},
  {value:'checked-out',label:'Active: Checked Out'},
  {value:'checked-in',label:'Only Checked In'},
  {value:'no-show',label:'No-Shows'},
  {value:'cancelled',label:'Cancelled'},
  {value:'all',label:'Active or Cancelled'},
] as const;
function today(zone:string){
  const p=new Intl.DateTimeFormat('en-CA',{timeZone:zone,
    year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(new Date());
  const get=(type:string)=>p.find(x=>x.type===type)?.value??'';
  return get('year')+'-'+get('month')+'-'+get('day');
}
const isDate=(value:string|null):value is string=>
  Boolean(value&&/^\d{4}-\d{2}-\d{2}$/.test(value));
export function ReservationsReportWorkspace({location,locations,onChooseLocation,onCalendar}:{
  location:Location;locations:Location[];
  onChooseLocation:(code:string)=>void;onCalendar:()=>void;
}){
  const initial=useMemo(()=>new URLSearchParams(window.location.search),[]);
  const current=today(location.timezone);
  const initialStatus=initial.get('status');
  const [filters,setFilters]=useState<Filters>({
    from:isDate(initial.get('from'))?initial.get('from')!:current,
    to:isDate(initial.get('to'))?initial.get('to')!:current,
    dateField:'beginning',
    status:stateOptions.some(x=>x.value===initialStatus)
      ?initialStatus as Filters['status']:'active',
    room:'',service:'',first:'any',member:'any',bookedBy:'',
  });
  const [data,setData]=useState<Data|null>(null);
  const [state,setState]=useState<'loading'|'ready'|'error'>('loading');
  const [message,setMessage]=useState('');
  const [exporting,setExporting]=useState(false);
  const params=useMemo(()=>{
    const q=new URLSearchParams({location:location.code,
      from:filters.from,to:filters.to,dateField:filters.dateField,
      status:filters.status,first:filters.first,member:filters.member});
    if(filters.room)q.set('room',filters.room);
    if(filters.service)q.set('service',filters.service);
    if(filters.bookedBy)q.set('bookedBy',filters.bookedBy);
    return q;
  },[location.code,filters]);
  useEffect(()=>{
    const controller=new AbortController();
    setState('loading');setMessage('');
    void fetch('/api/v1/staff/reports/reservations?'+params,{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async r=>{
      const body=await r.json() as {ok:boolean;data?:Data;error?:{code:string}};
      if(!r.ok||!body.ok||!body.data)throw Error(body.error?.code??'Report unavailable');
      if(!controller.signal.aborted){setData(body.data);setState('ready');}
    }).catch(e=>{
      if(!controller.signal.aborted){setMessage(e instanceof Error?e.message:'Report unavailable');
        setState('error');}
    });
    return ()=>controller.abort();
  },[params]);
  const update=<K extends keyof Filters>(key:K,value:Filters[K])=>
    setFilters(old=>({...old,[key]:value}));
  const exportCsv=async()=>{
    if(state!=='ready'||exporting)return;
    setExporting(true);setMessage('');
    try{
      const query=new URLSearchParams(params);query.set('format','csv');
      const response=await fetch('/api/v1/staff/reports/reservations?'+query,{
        credentials:'same-origin',cache:'no-store'});
      if(!response.ok)throw Error('CSV export unavailable');
      const blob=await response.blob();
      const url=URL.createObjectURL(blob);
      const anchor=document.createElement('a');
      anchor.href=url;anchor.download='reservations-from-'+filters.from+
        '-to-'+filters.to+'.csv';
      document.body.append(anchor);anchor.click();anchor.remove();
      setTimeout(()=>URL.revokeObjectURL(url),1000);
    }catch(e){setMessage(e instanceof Error?e.message:'CSV export unavailable');}
    finally{setExporting(false);}
  };
  const reportOptions=(values:SelectItem[])=>
    values.map(v=><option key={v.id} value={v.id}>{v.name}</option>);
  return <section className="ecfs-reservations-report"
    aria-label="Reservations Report">
    <header className="ecfs-reservation-report-title">
      <h2>Reservations at East Coast Float Spa {location.name}</h2>
      <button type="button" onClick={onCalendar}>Back to Calendar</button>
    </header>
    <div className="ecfs-reservation-report-filters">
      <select aria-label="Report location" value={location.code}
        onChange={e=>onChooseLocation(e.target.value)}>
        {locations.map(l=><option key={l.code} value={l.code}>
          East Coast Float Spa {l.name}</option>)}
      </select>
      <select aria-label="Reservation status" value={filters.status}
        onChange={e=>update('status',e.target.value as Filters['status'])}>
        {stateOptions.map(x=><option key={x.value} value={x.value}>{x.label}</option>)}
      </select>
      <select aria-label="Date filter field" value={filters.dateField}
        onChange={e=>update('dateField',e.target.value as Filters['dateField'])}>
        <option value="beginning">Beginning</option><option value="booked">Booked</option>
      </select>
      <input type="date" aria-label="From date" value={filters.from}
        onChange={e=>update('from',e.target.value)}/>
      <span>to</span>
      <input type="date" aria-label="To date" value={filters.to}
        onChange={e=>update('to',e.target.value)}/>
      <select aria-label="Report room" value={filters.room}
        onChange={e=>update('room',e.target.value)}>
        <option value="">Any Room</option>{reportOptions(data?.rooms??[])}
      </select>
      <select aria-label="Report service" value={filters.service}
        onChange={e=>update('service',e.target.value)}>
        <option value="">Any Service</option>{reportOptions(data?.services??[])}
      </select>
      <select aria-label="First visit" value={filters.first}
        onChange={e=>update('first',e.target.value as Filters['first'])}>
        <option value="any">1st</option>
        <option value="first">First Visit</option>
        <option value="returning">Returning</option>
      </select>
      <select aria-label="Member Status" value={filters.member}
        onChange={e=>update('member',e.target.value as Filters['member'])}>
        <option value="any">Member Status</option>
        <option value="member">Members (verified staging enrollments)</option>
        <option value="non-member" disabled>Non-Members (historical data pending)</option>
      </select>
      <select aria-label="Booked By" value={filters.bookedBy}
        onChange={e=>update('bookedBy',e.target.value)}>
        <option value="">Booked by Anyone</option>
        <option value="ONLINE">Booked Online</option>
        {reportOptions(data?.bookers??[])}
      </select>
      <button type="button" className="ecfs-report-csv" aria-label="Export Reservations CSV"
        onClick={()=>void exportCsv()} disabled={state!=='ready'||exporting}>
        {exporting?'Exporting…':'CSV ↓'}
      </button>
    </div>
    {state==='loading'&&<p role="status">Loading reservations…</p>}
    {state==='error'&&<p role="alert">{message}</p>}
    {state==='ready'&&data&&<>
      <div className="ecfs-reservation-report-stats">
        <div><strong>Count</strong><span>{data.summary.count}</span></div>
        <div><strong>Unique Customers</strong><span>{data.summary.uniqueCustomers}</span></div>
        <div><strong>Total Paid</strong><span>Not yet reconciled</span></div>
      </div>
      <p className="ecfs-report-accuracy">Historical Helm financial amounts and
        non-member verification are not yet in the staging ledger.
        CSV exports retain all 47 original columns and leave unverified amounts blank.
        Original numeric Helm Location ID is verified for Princeton only;
        West Chester and Yardley IDs remain blank until source-verified.</p>
      <div className="ecfs-report-table-scroll">
        <table className="ecfs-reservation-report-table">
          <thead><tr><th>Beginning</th><th>Customer</th><th>Service</th><th>Room</th>
            <th>Booked</th><th>Cancelled</th><th>Paid</th><th>Tax</th>
            <th>Gross</th></tr></thead>
          <tbody>{data.rows.map(row=><tr key={row.appointmentId}>
            <td>{row.Begins}</td>
            <td>{row['Customer First Name']+' '+row['Customer Last Name']}</td>
            <td>{row.Service}</td><td>{row.Room}</td>
            <td>{row['Booked At']} {row['Booked By']}</td>
            <td>{row['Cancelled At']}</td>
            <td>{row.Paid||'—'}</td><td>{row.Tax||'—'}</td>
            <td>{row['Total Paid']||'—'}</td>
          </tr>)}</tbody>
        </table>
        {!data.rows.length&&<p role="status">No matching reservations.</p>}
      </div>
    </>}
    {message&&state==='ready'&&<p role="alert">{message}</p>}
  </section>;
}
